import React, { useEffect } from 'react';
import Flatpickr from 'react-flatpickr';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import Popover from '@material-ui/core/Popover';
import monthSelectPlugin from 'flatpickr/dist/plugins/monthSelect';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import { Grid, Dialog, AppBar, Tabs, Tab, Box } from '@material-ui/core';
import utility from '../../../utility/services/utility';
import FormDropDown from './FormDropDown';
import { DATE_RANGE_META, CUSTOM_RANGE } from '../../../constants/formMeta/growthTrends';
import {
  subWeeks,
  differenceInCalendarDays,
  subDays,
  subYears,
  format,
  isAfter,
  startOfWeek,
  addWeeks,
  getWeekOfMonth,
  startOfMonth,
  addDays,
  subMonths,
  endOfMonth,
} from 'date-fns';
import { ArrowDropDown, ArrowDropUp, Close } from '@material-ui/icons';
import CustomMaskedInput from './MaskedInput';
import DateRangeIcon from '@material-ui/icons/DateRange';
import { useIsSubscriberUser } from '../../../utility/services/customHooks';
import { actionTypes } from '../../../constants/actionTypes';

const useStyles = makeStyles((theme) => ({
  typography: {
    padding: theme.spacing(3.13),
  },
}));

export default function DateRangePicker(props) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const reduxFilterState = useSelector((state) => state.root.datePickerFilter);
  const [anchorEl, setAnchorEl] = React.useState(null); //anchors to hold popup
  const previousPeriodLabel = props.isOpportunityExplorer ? 'Previous Complete Period' : 'Previous Period';
  const [showDatePicker, toogleDatePicker] = React.useState(props.defaultSecondPickerVisible); // to check if 2nd pop is visible
  const initialShowDatePickerRef = React.useRef(props.defaultSecondPickerVisible);
  const isSubscriberUser = useIsSubscriberUser();

  const [selectedOptions, setSelectionOtions] = React.useState({
    // to set default selected options
    dateRange: props.isRankingFactor
      ? { label: 'Last 90 Days', value: 'last 90 days' }
      : props.defaultSelected || { label: 'Last 12 Complete Weeks', value: 'last 12 Complete weeks' },
    compareRange: { label: previousPeriodLabel, value: 'previous period' },
  });

  useEffect(() => {
    if (props.defaultSecondPickerVisible) {
      updateComparisonPeriod(props.defaultDateRange);
    }
  }, []);

  useEffect(() => {
    toogleDatePicker(props.defaultSecondPickerVisible);
    initialShowDatePickerRef.current = props.defaultSecondPickerVisible;
  }, [props.defaultSecondPickerVisible]);

  useEffect(() => {
    if (reduxFilterState.dateRanges) {
      setUserSelectedDateRange(reduxFilterState.dateRanges);
    }
    if (reduxFilterState?.filters) {
      const isComparisonEnabled = reduxFilterState.filters.is_date_comparison == 'Yes';
      toogleDatePicker(isComparisonEnabled);
      initialShowDatePickerRef.current = isComparisonEnabled;
    }
    if (props.defaultDateRange) {
      setRangeData(props.defaultDateRange);
    }
    if (props.defaultSelected) {
      setSelectionOtions({
        dateRange: props.defaultSelected,
        compareRange: { label: previousPeriodLabel, value: 'previous period' },
      });
    }
  }, [reduxFilterState]);

  const [COMPARE_RANGE_META, setComparisonRange] = React.useState([{ ...CUSTOM_RANGE }]); // drowdown for compare range

  const [rangeData, setRangeData] = React.useState(
    props.defaultDateRange || {
      // all selected dates
      startDate1: subWeeks(startOfWeek(new Date()), 12),
      endDate1: subDays(startOfWeek(new Date()), 1),
      startDate2: subWeeks(startOfWeek(new Date()), 24),
      endDate2: subDays(subWeeks(startOfWeek(new Date()), 12), 1),
    }
  );

  const [errorArr, setError] = React.useState([]);

  const [isMobile, setMobile] = React.useState(null);

  const [value, setValue] = React.useState(1); // mobile tab values
  const [showError, setShowError] = React.useState(false);
  const [dateInvalidMsg, setDateInvalidMsg] = React.useState(null);
  const [userSelectedDateRange, setUserSelectedDateRange] = React.useState({
    selected: {
      startDate: props.defaultDateRange.startDate1,
      endDate: props.defaultDateRange.endDate1,
      label: selectedOptions.dateRange.label,
    },
    comparison: {
      startDate: props.defaultDateRange.startDate2,
      endDate: props.defaultDateRange.endDate2,
      label: showDatePicker ? selectedOptions.compareRange.label : 'Previous Period', // setting to default label that is used for showing period on summary panel if comparison period is removed
    },
  });

  React.useEffect(() => {
    const isMobile = document.documentElement.clientWidth < 768;
    setMobile(isMobile);
  }, []);

  const toggleError = (value, isRemove = false, msg) => {
    let arr = errorArr;
    if (msg) {
      setShowError(true);
      setDateInvalidMsg(msg);
      arr = arr.filter((i) => i !== 'selectedRange' || i !== 'comparisonRange');
    }
    if (isRemove) {
      arr = arr.filter((i) => i !== value);
    } else {
      arr.push(value);
    }
    setError([...new Set(arr)]);
  };

  const handleClick = (event) => {
    initialShowDatePickerRef.current = showDatePicker;
    setAnchorEl(event.currentTarget);
  };

  const handleDateRangeSelect = (date, isComparisonParam = false) => {
    if (!date || date.length < 2) {
      return;
    }
    const selectedDates = {};
    if (isComparisonParam) {
      selectedDates.startDate2 = date[0];
      selectedDates.endDate2 = date[1];
      selectedDates.label2 = 'Custom Date Range';
    } else {
      selectedDates.startDate1 = date[0];
      selectedDates.endDate1 = date[1];
      selectedDates.label1 = 'Custom Date Range';
      selectedDates.label2 = 'Custom Date Range';
    }
    setSelectionOtions({ ...selectedOptions, [isComparisonParam ? 'compareRange' : 'dateRange']: { ...CUSTOM_RANGE } });
    setRangeData({ ...rangeData, ...selectedDates });
    setError([]);
  };

  const handleDropDownChange = (res) => {
    setSelectionOtions({ ...selectedOptions, [res.type]: { label: res.label, value: res.value } });
    delete res.type;
    setRangeData({ ...rangeData, ...res });
    if (res.label.includes('Previous')) {
      updateComparisonPeriod({ ...rangeData, ...res }, res.label);
    }
    setError([]);
  };

  const applyFilters = () => {
    const dateRanges = {
      selected: {
        startDate: rangeData.startDate1,
        endDate: rangeData.endDate1,
        label: selectedOptions.dateRange.label,
      },
      comparison: {
        startDate: rangeData.startDate2,
        endDate: rangeData.endDate2,
        label: showDatePicker ? selectedOptions.compareRange.label : 'Previous Period', // setting to default label that is used for showing period on summary panel if comparison period is removed
      },
    };
    if (props.onChange) {
      props.onChange(rangeData, showDatePicker);
    }
    props.setDateRanges(dateRanges);
    setUserSelectedDateRange(dateRanges);
    setAnchorEl(null);
  };

  const handleClose = () => {
    // Revert the comparison-panel toggle if the user cancels without applying,
    // otherwise the navbar label keeps showing "Compare to:" after Cancel.
    toogleDatePicker(initialShowDatePickerRef.current);
    setAnchorEl(null);
  };

  const clearComparisonPeriod = React.useCallback(() => {
    const dateRanges = {
      selected: {
        startDate: rangeData.startDate1,
        endDate: rangeData.endDate1,
        label: selectedOptions.dateRange.label,
      },
      comparison: {
        startDate: rangeData.startDate2,
        endDate: rangeData.endDate2,
        label: previousPeriodLabel,
      },
    };

    toogleDatePicker(false);
    initialShowDatePickerRef.current = false;

    if (reduxFilterState?.filters || reduxFilterState?.dateRanges) {
      dispatch({
        type: actionTypes.SET_GLOBAL_DATE_RANGE_FILTERS,
        filtersData: {
          ...reduxFilterState,
          filters: {
            ...(reduxFilterState?.filters || {}),
            is_date_comparison: 'No',
          },
          dateRanges,
        },
      });
    }

    if (props.onChange) {
      props.onChange(rangeData, false);
    }
    props.setDateRanges(dateRanges);
    setUserSelectedDateRange(dateRanges);
    setAnchorEl(null);
  }, [dispatch, previousPeriodLabel, props, rangeData, reduxFilterState, selectedOptions.dateRange.label]);

  useEffect(() => {
    if (!props.isHeader) return;

    const handleExternalClear = () => {
      clearComparisonPeriod();
    };

    window.addEventListener('ai-vis-clear-comparison', handleExternalClear);
    return () => window.removeEventListener('ai-vis-clear-comparison', handleExternalClear);
  }, [clearComparisonPeriod, props.isHeader]);

  const updateComparisonPeriod = (range, label = '') => {
    const isPrevYearSamePeriod = label.includes('Previous Year Same Period');
    const isPrevPeriod = label.includes('Previous Period') || props.isOpportunityExplorer;

    const diff = differenceInCalendarDays(range.endDate1, range.startDate1);

    const isCompleteWeeksSelected =
      selectedOptions.dateRange.label && selectedOptions.dateRange.label.includes('Complete Weeks');
    const isCompleteMonthsSelected =
      selectedOptions.dateRange.label && selectedOptions.dateRange.label.includes('Complete Months');

    let startOfPrevPeriod,
      endOfPrevPeriod = null;
    if (isCompleteWeeksSelected && isPrevYearSamePeriod) {
      const weekIndex = getWeekOfMonth(range.startDate1);
      const previousYearDate = subYears(range.startDate1, 1);
      const startOfPrevMonth = startOfMonth(previousYearDate);
      const weekDayOfPrevMonth = addWeeks(startOfPrevMonth, weekIndex - 1);
      startOfPrevPeriod = startOfWeek(weekDayOfPrevMonth);
      const noOfWeeks = Math.abs(
        utility.getDiffAsPerGranularityType('week', range.startDate1, addDays(range.endDate1, 1))
      );
      endOfPrevPeriod = subDays(addWeeks(startOfPrevPeriod, noOfWeeks), 1);
    }

    if (isPrevPeriod && isCompleteMonthsSelected && !isPrevYearSamePeriod) {
      const noOfMonths = Math.abs(
        utility.getDiffAsPerGranularityType('month', rangeData.startDate1, addDays(rangeData.endDate1, 1))
      );
      startOfPrevPeriod = startOfMonth(
        subMonths(rangeData.startDate1, noOfMonths + (props.isOpportunityExplorer ? 1 : 0))
      ); // adding a month to avoid ongoing month
      endOfPrevPeriod = endOfMonth(subMonths(rangeData.startDate1, props.isOpportunityExplorer ? 2 : 1));
    }

    const startDate =
      isPrevYearSamePeriod || (isPrevPeriod && isCompleteMonthsSelected)
        ? startOfPrevPeriod || subYears(range.startDate1, 1)
        : range.prevStartDate
        ? range.prevStartDate
        : subDays(range.startDate1, diff + 1);
    const endDate =
      isPrevYearSamePeriod || isCompleteMonthsSelected
        ? endOfPrevPeriod || subYears(range.endDate1, 1)
        : range.prevEndDate
        ? range.prevEndDate
        : subDays(range.startDate1, 1);
    setRangeData({ ...rangeData, startDate2: startDate, endDate2: endDate });
    setComparisonRange([
      { ...CUSTOM_RANGE },
      {
        label: previousPeriodLabel,
        value: 'previous period',
        startDate: range.prevStartDate ? range.prevStartDate : subDays(range.startDate1, diff + 1),
        endDate: range.prevEndDate ? range.prevEndDate : subDays(range.startDate1, 1),
      },
      {
        label: 'Previous Year Same Period',
        value: 'previous year same period',
        startDate: subYears(range.startDate1, 1),
        endDate: subYears(range.endDate1, 1),
      },
    ]);
  };

  const toogleComparisonPriod = () => {
    toogleDatePicker(true);
    updateComparisonPeriod(rangeData);
  };

  const onDateChange = (date, type) => {
    const TempRangeData = { ...rangeData };
    TempRangeData[type] = new Date(date);
    if (isAfter(TempRangeData.startDate1, TempRangeData.endDate1)) {
      toggleError('selectedRange');
      setDateInvalidMsg(null);

      setShowError(true);
    } else if (isAfter(TempRangeData.startDate2, TempRangeData.endDate2)) {
      toggleError('comparisonRange');
      setDateInvalidMsg(null);
    } else {
      setSelectionOtions({
        ...selectedOptions,
        [type.includes('2') ? 'compareRange' : 'dateRange']: { ...CUSTOM_RANGE },
      });
      setRangeData(TempRangeData);
      setShowError(false);
      setDateInvalidMsg(null);
    }
  };

  function a11yProps(index) {
    return {
      id: `full-width-tab-${index}`,
      'aria-controls': `full-width-tabpanel-${index}`,
    };
  }

  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;

  const Actions = () => (
    <Grid container className="buttons-inline" spacing={2}>
      <Button id="content_recommendation__button__date_range_picker_0" onClick={handleClose} color="primary">
        <span id="content_recommendation__buttontext__date_range_picker_1">Cancel</span>
      </Button>
      <Button
        id="content_recommendation__button__date_range_picker_2"
        onClick={applyFilters}
        variant="contained"
        color="primary"
      >
        <span id="content_recommendation__buttontext__date_range_picker_3">Apply</span>
      </Button>
    </Grid>
  );

  const SelectRangeFilter = (
    <Typography className={classes.typography}>
      <Grid id="content_recommendation__grid__date_range_picker_4" container className={classes.root}>
        <Grid id="content_recommendation__buttontext__date_range_picker_5" xs={12}>
          {(!isMobile || (isMobile && !showDatePicker)) && (
            <Grid id="content_recommendation__buttontext__date_range_picker_6" as={12}>
              <div id="content_recommendation__buttontext__date_range_picker_7" className="mb-10">
                {props.selectionPeriodLabel || 'Current Period'}
              </div>
            </Grid>
          )}
          <Grid id="content_recommendation__buttontext__date_range_picker_8" xs={12}>
            <div onClick={() => toogleComparisonPriod()} className={`link-button ${showDatePicker ? 'hidden' : ''}`}>
              + compare {props.comparisonPeriodLabel || 'Previous Period'}
            </div>
          </Grid>
          <Grid id="content_recommendation__buttontext__date_range_picker_8" xs={12}>
            <FormDropDown
              id="content_recommendation__formdropdown__date_range_picker_9"
              value={selectedOptions.dateRange}
              label={'Date Range'}
              onChange={(res) =>
                handleDropDownChange({
                  ...rangeData,
                  ...res,
                  startDate1: res.startDate,
                  endDate1: res.endDate || new Date(),
                  type: 'dateRange',
                })
              }
              hierarchicalSelect
              options={props.DATE_RANGE_META || DATE_RANGE_META}
            />
          </Grid>
          <Grid
            id="content_recommendation__grid__date_range_picker_10"
            container
            className={props.showMonthPicker ? 'months-container' : 'dates-container'}
          >
            <Grid id="content_recommendation__grid__date_range_picker_11" xs={6}>
              <div
                id="content_recommendation__div__date_range_picker_12"
                className={`from-date ${
                  (errorArr.includes('startDate1') && showError) || (errorArr.includes('selectedRange') && showError)
                    ? 'error'
                    : ''
                }`}
              >
                {props.showMonthPicker ? (
                  <>
                    <div
                      id="content_recommendation__div__date_range_picker_13"
                      className="single-month-picker position-rel w-140"
                    >
                      <label className="position-abs custom-input-label">From Month</label>
                      <Flatpickr
                        name="startDate1"
                        key={rangeData.startDate1}
                        onChange={(e) => {
                          if (e && e.length) {
                            onDateChange(startOfMonth(e[0]), 'startDate1');
                          }
                        }}
                        options={{
                          disableMobile: true,
                          defaultDate: rangeData.startDate1,
                          plugins: [
                            new monthSelectPlugin({
                              shorthand: true, //defaults to false
                              dateFormat: 'M Y', //defaults to "F Y"
                              altFormat: 'F Y', //defaults to "F Y"
                            }),
                          ],
                        }}
                      ></Flatpickr>
                    </div>
                  </>
                ) : (
                  <>
                    <div id="content_recommendation__div__date_range_picker_14" className="title-text">
                      From Date
                    </div>
                    <CustomMaskedInput
                      setError={toggleError}
                      value={format(rangeData.startDate1, 'MM/dd/yyyy')}
                      onChange={(e) => onDateChange(e, 'startDate1')}
                      placeholder="MM/DD/YYYY"
                      errorRef="startDate1"
                      tabIndex={3}
                    />
                  </>
                )}
              </div>
            </Grid>
            <Grid id="content_recommendation__grid__date_range_picker_15" xs={6}>
              <div
                id="content_recommendation__div__date_range_picker_16"
                className={`to-date ${
                  (errorArr.includes('endDate1') && showError) || (errorArr.includes('selectedRange') && showError)
                    ? 'error'
                    : ''
                }`}
              >
                {props.showMonthPicker ? (
                  <>
                    <div
                      id="content_recommendation__div__date_range_picker_17"
                      className="single-month-picker position-rel w-140"
                    >
                      <label className="position-abs custom-input-label">To Month</label>
                      <Flatpickr
                        name="endDate1"
                        key={rangeData.endDate1}
                        onChange={(e) => {
                          if (e && e.length) {
                            onDateChange(endOfMonth(e[0]), 'endDate1');
                          }
                        }}
                        options={{
                          disableMobile: true,
                          defaultDate: rangeData.endDate1,
                          plugins: [
                            new monthSelectPlugin({
                              shorthand: true, //defaults to false
                              dateFormat: 'M Y', //defaults to "F Y"
                              altFormat: 'F Y', //defaults to "F Y"
                            }),
                          ],
                        }}
                      ></Flatpickr>
                    </div>
                  </>
                ) : (
                  <>
                    <div id="content_recommendation__div__date_range_picker_18" className="title-text">
                      To Date
                    </div>
                    <CustomMaskedInput
                      setError={toggleError}
                      value={format(rangeData.endDate1, 'MM/dd/yyyy')}
                      onChange={(e) => onDateChange(e, 'endDate1')}
                      placeholder="MM/DD/YYYY"
                      errorRef="endDate1"
                      tabIndex={4}
                    />
                  </>
                )}
              </div>
            </Grid>
          </Grid>
          {dateInvalidMsg == null && showError && errorArr.includes('selectedRange') && (
            <p className="negative-text">
              {' '}
              {"'"}To Date{"'"} should be after {"'"}From Date{"'"}{' '}
            </p>
          )}
          {dateInvalidMsg && (errorArr.includes('startDate1') || errorArr.includes('endDate1')) ? (
            <p className={`negative-text ${errorArr.includes('startDate1') ? 'left-align' : 'right-align'}`}>
              {dateInvalidMsg}
            </p>
          ) : null}

          <Grid id="content_recommendation__grid__date_range_picker_14" container justify="center" spacing={2}>
            <Grid id="content_recommendation__grid__date_range_picker_15" item>
              <div
                id="content_recommendation__div__date_range_picker_16"
                className={`flatpickr-override ${props.showMonthPicker ? 'd-none' : ''}`}
              >
                <Flatpickr
                  className="d-none"
                  options={{
                    mode: 'range',
                    inline: true,
                    defaultDate: [new Date(), subYears(new Date(), 1)],
                  }}
                  value={[rangeData.startDate1, rangeData.endDate1]}
                  onChange={(date) => handleDateRangeSelect(date)}
                />
              </div>
            </Grid>
          </Grid>
          {(!showDatePicker || (isMobile && showDatePicker)) && <Actions />}
        </Grid>
      </Grid>
    </Typography>
  );

  const CompareRangeFilter = (
    <Typography className={classes.typography}>
      <Grid id="content_recommendation__grid__date_range_picker_17" container className={classes.root}>
        <Grid id="content_recommendation__grid__date_range_picker_18" xs={12}>
          {(!isMobile || (isMobile && !showDatePicker)) && (
            <Grid id="content_recommendation__grid__date_range_picker_19" as={12}>
              <div id="content_recommendation__div__date_range_picker_20" className="mb-10">
                {props.comparisonPeriodLabel || selectedOptions.compareRange.label}
                <Close onClick={handleClose} style={{ margin: '-20', float: 'right', cursor: 'pointer' }} />
              </div>
            </Grid>
          )}
          <Grid id="content_recommendation__grid__date_range_picker_21" xs={12}>
            <div
              id="content_recommendation__div__date_range_picker_22"
              onClick={() => toogleDatePicker(null)}
              className="link-button red"
            >
              - Remove {props.comparisonPeriodLabel || 'Previous Period'}
            </div>
          </Grid>
          <Grid id="content_recommendation__grid__date_range_picker_23" xs={12}>
            <FormDropDown
              id="content_recommendation__formdropdown__date_range_picker_24"
              value={selectedOptions.compareRange}
              label={'Date Range'}
              hierarchicalSelect
              onChange={(res) =>
                handleDropDownChange(
                  {
                    ...rangeData,
                    ...res,
                    startDate2: res.startDate,
                    endDate2: res.endDate || new Date(),
                    type: 'compareRange',
                  },
                  true
                )
              }
              options={COMPARE_RANGE_META}
            />
          </Grid>
          <Grid
            id="content_recommendation__grid__date_range_picker_25"
            container
            className={props.showMonthPicker ? 'months-container' : 'dates-container'}
          >
            <Grid id="content_recommendation__grid__date_range_picker_26" xs={6}>
              <div
                id="content_recommendation__div__date_range_picker_27"
                className={`from-date ${
                  errorArr.includes('startDate2') || errorArr.includes('comparisonRange') ? 'error' : ''
                }`}
              >
                {props.showMonthPicker ? (
                  <>
                    <div
                      id="content_recommendation__div__date_range_picker_28"
                      className="single-month-picker position-rel w-140"
                    >
                      <label className="position-abs custom-input-label">From Month</label>
                      <Flatpickr
                        name="startDate2"
                        key={rangeData.startDate2}
                        onChange={(e) => {
                          if (e && e.length) {
                            onDateChange(startOfMonth(e[0]), 'startDate2');
                          }
                        }}
                        options={{
                          disableMobile: true,
                          defaultDate: rangeData.startDate2,
                          plugins: [
                            new monthSelectPlugin({
                              shorthand: true, //defaults to false
                              dateFormat: 'M Y', //defaults to "F Y"
                              altFormat: 'F Y', //defaults to "F Y"
                            }),
                          ],
                        }}
                      ></Flatpickr>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="title-text">From Date</div>
                    <CustomMaskedInput
                      setError={toggleError}
                      value={format(rangeData.startDate2, 'MM/dd/yyyy')}
                      onChange={(e) => onDateChange(e, 'startDate2')}
                      placeholder="MM/DD/YYYY"
                      errorRef="startDate2"
                      tabIndex={5}
                    />
                  </>
                )}
              </div>
            </Grid>
            <Grid id="content_recommendation__grid__date_range_picker_29" xs={6}>
              <div
                id="content_recommendation__div__date_range_picker_30"
                className={`to-date ${
                  errorArr.includes('endDate2') || errorArr.includes('comparisonRange') ? 'error' : ''
                }`}
              >
                {props.showMonthPicker ? (
                  <>
                    <div
                      id="content_recommendation__div__date_range_picker_31"
                      className="single-month-picker position-rel w-140"
                    >
                      <label className="position-abs custom-input-label">To Month</label>
                      <Flatpickr
                        name="endDate2"
                        key={rangeData.endDate2}
                        onChange={(e) => {
                          if (e && e.length) {
                            onDateChange(endOfMonth(e[0]), 'endDate2');
                          }
                        }}
                        options={{
                          disableMobile: true,
                          defaultDate: rangeData.endDate2,
                          plugins: [
                            new monthSelectPlugin({
                              shorthand: true, //defaults to false
                              dateFormat: 'M Y', //defaults to "F Y"
                              altFormat: 'F Y', //defaults to "F Y"
                            }),
                          ],
                        }}
                      ></Flatpickr>
                    </div>
                  </>
                ) : (
                  <>
                    <div id="content_recommendation__div__date_range_picker_32" className="title-text">
                      To Date
                    </div>
                    <CustomMaskedInput
                      setError={toggleError}
                      value={format(rangeData.endDate2, 'MM/dd/yyyy')}
                      onChange={(e) => onDateChange(e, 'endDate2')}
                      placeholder="MM/DD/YYYY"
                      errorRef="endDate2"
                      tabIndex={6}
                    />
                  </>
                )}
              </div>
            </Grid>
          </Grid>
          {dateInvalidMsg == null && errorArr.includes('comparisonRange') && (
            <p className="negative-text">
              {' '}
              {"'"}To Date{"'"} should be after {"'"}From Date{"'"}{' '}
            </p>
          )}
          {dateInvalidMsg && (errorArr.includes('startDate2') || errorArr.includes('endDate2')) ? (
            <p className={`negative-text ${errorArr.includes('startDate2') ? 'left-align' : 'right-align'}`}>
              {dateInvalidMsg}
            </p>
          ) : null}
          <Grid id="content_recommendation__grid__date_range_picker_33" container justify="center" spacing={2}>
            <Grid id="content_recommendation__grid__date_range_picker_34" item>
              <div
                id="content_recommendation__div__date_range_picker_35"
                className={`flatpickr-override ${props.showMonthPicker ? 'd-none' : ''}`}
              >
                <Flatpickr
                  className="d-none"
                  options={{
                    mode: 'range',
                    inline: true,
                    defaultDate: [new Date(), subYears(new Date(), 1)],
                  }}
                  value={[rangeData.startDate2, rangeData.endDate2]}
                  onChange={(date) => handleDateRangeSelect(date, true)}
                />
              </div>
            </Grid>
          </Grid>
          <Actions />
        </Grid>
      </Grid>
    </Typography>
  );

  function TabPanel(props) {
    const { children, value, index, ...other } = props;

    return (
      <Typography
        component="div"
        role="tabpanel"
        hidden={value !== index}
        id={`wrapped-tabpanel-${index}`}
        aria-labelledby={`wrapped-tab-${index}`}
        {...other}
      >
        {value === index && <Box p={3}>{children}</Box>}
      </Typography>
    );
  }
  return (
    <Grid
      id="content_recommendation__grid__date_range_picker_36"
      item
      className={
        props.isHeader
          ? props?.class
            ? `d-inline-block align-self-center ${props?.class}`
            : 'd-inline-block align-self-center'
          : ''
      }
    >
      <div id="content_recommendation__div__date_range_picker_37" className="popover-button dflex ">
        <Button
          id="filter-cta"
          onClick={handleClick}
          className={`btnDropdown  ${props.isHeader ? 'header-datepicker-button' : ''}`}
        >
          <DateRangeIcon color="primary" />
          <div id="content_recommendation__div__date_range_picker_38" className={!props.isHeader ? '' : 'pl-10'}>
            {showDatePicker && props.isHeader && !props.isTopTrendDetails ? (
              <>
                <div>{`${format(userSelectedDateRange.selected.startDate, 'yyyy/MM/dd')} - ${format(
                  userSelectedDateRange.selected.endDate,
                  'yyyy/MM/dd'
                )}`}</div>
                {/* comparisonPrefix replaces "Compare to :", e.g. "vs" in a screen's filter row */}
                <div className={props.comparisonPrefix ? 'date-range-comparison-line' : undefined}>
                  {props.comparisonPrefix ? `${props.comparisonPrefix} ` : <> Compare to :</>}
                  {`${format(userSelectedDateRange.comparison.startDate, 'yyyy/MM/dd')} - ${format(
                    userSelectedDateRange.comparison.endDate,
                    'yyyy/MM/dd'
                  )}`}
                </div>
              </>
            ) : showDatePicker ? (
              'Two Periods Comparison'
            ) : props.isTopTrendDetails ? (
              selectedOptions.dateRange?.label &&
              selectedOptions.dateRange.label !== 'Custom' &&
              DATE_RANGE_META.some((option) => option.label === selectedOptions.dateRange.label) ? (
                selectedOptions.dateRange.label
              ) : (
                'Custom'
              )
            ) : props.isOpportunityExplorer ? (
              selectedOptions.dateRange.label
            ) : userSelectedDateRange.selected.label != 'Custom' ? (
              userSelectedDateRange.selected.label || 'Last 12 Complete Weeks'
            ) : (
              `${format(userSelectedDateRange.selected.startDate, 'yyyy/MM/dd')} - ${format(
                userSelectedDateRange.selected.endDate,
                'yyyy/MM/dd'
              )}`
            )}
          </div>
          {!open ? <ArrowDropDown fontSize="large" /> : <ArrowDropUp fontSize="large" />}
        </Button>
        {isMobile ? (
          <Dialog fullScreen open={open} onClose={handleClose}>
            {!showDatePicker ? (
              SelectRangeFilter
            ) : (
              <>
                <AppBar position="static" color="#fff" className="dialog-tabs">
                  <Tabs
                    value={value}
                    onChange={(event, newValue) => setValue(newValue)}
                    indicatorColor="primary"
                    textColor="primary"
                    variant="fullWidth"
                    aria-label="full width tabs example"
                  >
                    <Tab label="Current Period" {...a11yProps(0)} />
                    <Tab label="Previous Period" {...a11yProps(1)} />
                  </Tabs>
                </AppBar>
                <TabPanel value={value} index={0}>
                  {SelectRangeFilter}
                </TabPanel>
                <TabPanel value={value} index={1}>
                  {CompareRangeFilter}
                </TabPanel>
              </>
            )}
          </Dialog>
        ) : (
          <Popover
            id={id}
            open={open}
            anchorEl={anchorEl}
            onClose={handleClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            className="popWrapper"
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
          >
            {SelectRangeFilter}
            {showDatePicker && CompareRangeFilter}
          </Popover>
        )}
      </div>
    </Grid>
  );
}
